/**
 * /admin/marketplace — pack curation.
 *
 * Pack list with publish / unpublish, inline price edit, delete
 * (auto-refunding paid buyers), and the per-type creation wizards.
 *
 * 2026-07-31 — the per-user Install / Uninstall admin actions were
 * removed (founder call). Users acquire packs only through the
 * marketplace Buy flow; revoking is done via pack delete-with-refunds.
 *
 * Full pack editor (create / edit / players / teams CRUD) is Phase 6.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import {
  RefreshCw, CheckCircle2, Users,
  Eye, EyeOff, Trash2, AlertTriangle, Pencil, Check, X,
  UserCheck, MapPin, Trophy,
} from "lucide-react";
import {
  listPacks,
  publishPack,
  unpublishPack,
  updatePack,
  deletePack,
  deletePackWithRefunds,
} from "../../api/owner.api";
import SquadPackWizard from "./SquadPackWizard";
import OfficialsPackWizard from "./OfficialsPackWizard";
import VenuePackWizard from "./VenuePackWizard";
import TournamentBundleWizard from "./TournamentBundleWizard";
import { toArray } from "../../utils/toArray";

// Pack types that have a composer wizard capable of round-tripping a
// saved draft. TOURNAMENT_BUNDLE composes child packs rather than
// catalog rows and has its own flow, so it is not editable here.
const EDITABLE_PACK_TYPES = new Set([
  "SQUAD_PACK", "OFFICIALS_PACK", "VENUE_PACK",
]);

export default function AdminMarketplace() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null); // `${packId}:${action}`
  const [flash, setFlash] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // pack pending delete confirm
  // 2026-08-03 — reopen a saved draft in its composer wizard to change the
  // picks (add players to a squad, swap a venue) before it is bundled and
  // published. Holds the pack row being edited; the matching wizard is
  // rendered with editPackId so it hydrates from the saved pack.
  //
  // Drafts only. Re-composing a LIVE pack would rewrite its template rows
  // while existing buyers keep the clone they already paid for, so the
  // pack and what buyers hold would silently diverge.
  const [editTarget, setEditTarget] = useState(null);
  // 2026-06-11 — inline price editor. `editingPriceId` holds the pack
  // id currently being edited; `priceDraft` is the string value bound
  // to the <input>. Save calls PATCH /packs/<id>/ with the new
  // price_credits — backend already permits it (PackDetailSerializer
  // doesn't mark price_credits read-only). Buyer snapshots are
  // unaffected because PackPurchase.price_credits_paid is a copy at
  // buy time (see Pack model docstring).
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [priceDraft, setPriceDraft] = useState("");
  // 2026-08-01 — inline name editor, same shape as the price one above.
  // Founder ask: rename a bundle (or any pack) after it's been created,
  // without a full pack-editor screen. PATCH /packs/<id>/ already accepts
  // `name` — PackDetailSerializer doesn't mark it read-only.
  //
  // Deliberately does NOT touch `slug`: the slug is derived once in the
  // backend's perform_create and is what any existing link points at, so
  // renaming stays a display-only change. Buyers' PackPurchase rows join
  // by FK, so past buyers see the new name too — which is the intent.
  const [editingNameId, setEditingNameId] = useState(null);
  const [nameDraft, setNameDraft] = useState("");
  // 2026-07-06 — pack-type filter. Founder ask: split the (long) list
  // into the four pack types the admin actually creates (SQUAD_PACK,
  // OFFICIALS_PACK, VENUE_PACK, MATCH_BUNDLE) so it's easy to find one.
  // Client-side filter — packs are already fully loaded by fetchPacks;
  // no backend round-trip needed on filter change.
  const [packTypeFilter, setPackTypeFilter] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  // 2026-08-03 — MATCH_BUNDLE creation removed from the console
  // (founder ask). The wizard, its "New MATCH_BUNDLE" button and every
  // ?action=new&type=MATCH_BUNDLE deep link are gone; existing bundles
  // remain listed / publishable / deletable below.
  const squadWizardOpen = searchParams.get("action") === "new"
    && searchParams.get("type") === "SQUAD_PACK";
  const officialsWizardOpen = searchParams.get("action") === "new"
    && searchParams.get("type") === "OFFICIALS_PACK";
  const venueWizardOpen = searchParams.get("action") === "new"
    && searchParams.get("type") === "VENUE_PACK";
  const bundleWizardOpen = searchParams.get("action") === "new"
    && searchParams.get("type") === "TOURNAMENT_BUNDLE";
  function closeWizard() {
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    next.delete("type");
    setSearchParams(next, { replace: true });
  }
  function openWizardByType(type) {
    const next = new URLSearchParams(searchParams);
    next.set("action", "new");
    next.set("type", type);
    setSearchParams(next);
  }
  const openSquadPackWizard = () => openWizardByType("SQUAD_PACK");
  const openOfficialsPackWizard = () => openWizardByType("OFFICIALS_PACK");
  const openVenuePackWizard = () => openWizardByType("VENUE_PACK");
  const openTournamentBundleWizard = () => openWizardByType("TOURNAMENT_BUNDLE");

  async function fetchPacks() {
    setLoading(true);
    try {
      const { data } = await listPacks();
      // DRF paginated → results[]; the admin viewset is paginated.
      // toArray normalises bare-array, paginated, and bad-shape responses.
      setPacks(toArray(data));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchPacks(); }, []);

  async function handlePublishToggle(pack) {
    const goLive = !pack.is_published;
    if (goLive && !confirm(
      `Publish "${pack.name}"? This will fan out a WhatsApp + email + in-app notification to every B2C subscriber (deduped 6h per channel).`,
    )) return;
    setBusyKey(`${pack.id}:publish`);
    setFlash(null);
    try {
      if (goLive) {
        const { data } = await publishPack(pack.id, { skipPromotion: false });
        const sent = (data.promotion || []).filter((p) => p.status === "sent").length;
        setFlash({
          ok: true,
          msg: `"${data.name}" is live. ${sent}/${(data.promotion || []).length} channels fired.`,
        });
      } else {
        const { data } = await unpublishPack(pack.id);
        setFlash({ ok: true, msg: `"${data.name}" unpublished. Existing buyers keep access.` });
      }
      fetchPacks();
    } catch (e) {
      setFlash({ ok: false, msg: e.response?.data?.detail || e.message });
    } finally {
      setBusyKey(null);
    }
  }

  async function handleDelete(pack) {
    setBusyKey(`${pack.id}:delete`);
    setFlash(null);
    try {
      // No purchases — plain DELETE is fine and avoids the refund branch.
      if ((pack.purchase_count || 0) === 0) {
        await deletePack(pack.id);
        setFlash({ ok: true, msg: `"${pack.name}" deleted. No purchases to refund.` });
      } else {
        const { data } = await deletePackWithRefunds(pack.id);
        const refunded = (data.refunds || []).reduce((a, r) => a + r.refunded_zc, 0);
        const skippedCount = (data.skipped || []).length;
        setFlash({
          ok: true,
          msg: `"${data.pack_name}" deleted. ${data.purchases_deleted} purchase(s) removed, ${data.refunds.length} buyer(s) refunded ${refunded} ZC total${skippedCount ? `, ${skippedCount} skipped (no debit_tx)` : ""}.`,
        });
      }
      setDeleteTarget(null);
      fetchPacks();
    } catch (e) {
      setFlash({ ok: false, msg: e.response?.data?.detail || e.message });
    } finally {
      setBusyKey(null);
    }
  }

  function startNameEdit(pack) {
    setEditingNameId(pack.id);
    setNameDraft(pack.name ?? "");
    setFlash(null);
  }

  function cancelNameEdit() {
    setEditingNameId(null);
    setNameDraft("");
  }

  async function saveNameEdit(pack) {
    const next = nameDraft.trim();
    if (!next) {
      setFlash({ ok: false, msg: "Pack name can't be empty." });
      return;
    }
    // Pack.name is max_length=200 — reject client-side so the operator
    // gets a sentence instead of a DRF field-error blob.
    if (next.length > 200) {
      setFlash({ ok: false, msg: "Pack name must be 200 characters or fewer." });
      return;
    }
    if (next === pack.name) {
      cancelNameEdit();
      return;
    }
    const prevName = pack.name;
    setBusyKey(`${pack.id}:name`);
    setFlash(null);
    try {
      const { data } = await updatePack(pack.id, { name: next });
      // Patch in place so the cell flips back to read-mode without
      // re-issuing the list call (same approach as savePriceEdit).
      setPacks((prev) => prev.map((row) =>
        row.id === pack.id ? { ...row, name: data.name } : row,
      ));
      setFlash({ ok: true, msg: `"${prevName}" renamed to "${data.name}".` });
      cancelNameEdit();
    } catch (e) {
      // DRF returns {name: ["..."]} for field errors, not {detail}.
      const fieldErr = e.response?.data?.name?.[0];
      setFlash({ ok: false, msg: fieldErr || e.response?.data?.detail || e.message });
    } finally {
      setBusyKey(null);
    }
  }

  function startPriceEdit(pack) {
    setEditingPriceId(pack.id);
    setPriceDraft(String(pack.price_credits ?? ""));
    setFlash(null);
  }

  function cancelPriceEdit() {
    setEditingPriceId(null);
    setPriceDraft("");
  }

  async function savePriceEdit(pack) {
    const raw = priceDraft.trim();
    const next = Number.parseInt(raw, 10);
    if (!Number.isInteger(next) || next < 0) {
      setFlash({ ok: false, msg: "Price must be a non-negative whole number of ZenCredits." });
      return;
    }
    if (next === (pack.price_credits ?? 0)) {
      cancelPriceEdit();
      return;
    }
    setBusyKey(`${pack.id}:price`);
    setFlash(null);
    try {
      const { data } = await updatePack(pack.id, { price_credits: next });
      // Patch the row in-place so the cell flips back to read-mode
      // with the new value without re-issuing the list call.
      setPacks((prev) => prev.map((row) =>
        row.id === pack.id ? { ...row, price_credits: data.price_credits } : row,
      ));
      setFlash({ ok: true, msg: `"${pack.name}" repriced to ${data.price_credits} ZC.` });
      cancelPriceEdit();
    } catch (e) {
      setFlash({ ok: false, msg: e.response?.data?.detail || e.message });
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Marketplace packs</h1>
          <p className="text-sm text-text-secondary mt-1">
            Curate packs, publish to fan-out across WhatsApp / email / in-app,
            or pre-grant a pack to a tester.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={openTournamentBundleWizard}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold"
            title="Wrap saved squad / officials / venues packs into one publishable tournament bundle."
          >
            <Trophy className="w-3.5 h-3.5" />
            New Tournament bundle
          </button>
          <button
            onClick={openSquadPackWizard}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-text-primary text-sm hover:bg-bg-secondary"
            title="Save a squad draft. Publishing happens via a Tournament bundle."
          >
            <Users className="w-3.5 h-3.5" />
            Squad draft
          </button>
          <button
            onClick={openOfficialsPackWizard}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-text-primary text-sm hover:bg-bg-secondary"
            title="Save an officials draft. Publishing happens via a Tournament bundle."
          >
            <UserCheck className="w-3.5 h-3.5" />
            Officials draft
          </button>
          <button
            onClick={openVenuePackWizard}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-text-primary text-sm hover:bg-bg-secondary"
            title="Save a venues draft. Publishing happens via a Tournament bundle."
          >
            <MapPin className="w-3.5 h-3.5" />
            Venues draft
          </button>
          <button
            onClick={fetchPacks}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      {/* Child wizards: onCreated refreshes the pack list without
          closing so the founder can iterate (Save draft → tweak → Save
          again) in one session. Close via Cancel / X or the wizard's
          own explicit "Done" path. */}
      {squadWizardOpen && (
        <SquadPackWizard
          onClose={closeWizard}
          onCreated={() => { fetchPacks(); }}
        />
      )}

      {officialsWizardOpen && (
        <OfficialsPackWizard
          onClose={closeWizard}
          onCreated={() => { fetchPacks(); }}
        />
      )}

      {venueWizardOpen && (
        <VenuePackWizard
          onClose={closeWizard}
          onCreated={() => { fetchPacks(); }}
        />
      )}

      {bundleWizardOpen && (
        <TournamentBundleWizard
          onClose={closeWizard}
          onCreated={() => { fetchPacks(); }}
        />
      )}

      {/* Edit an existing draft — same wizards as creation, hydrated from
          the saved pack. Keyed by pack id so switching drafts remounts
          rather than reusing the previous draft's state. */}
      {editTarget?.pack_type === "SQUAD_PACK" && (
        <SquadPackWizard
          key={editTarget.id}
          editPackId={editTarget.id}
          onClose={() => setEditTarget(null)}
          onCreated={() => { fetchPacks(); }}
        />
      )}
      {editTarget?.pack_type === "OFFICIALS_PACK" && (
        <OfficialsPackWizard
          key={editTarget.id}
          editPackId={editTarget.id}
          onClose={() => setEditTarget(null)}
          onCreated={() => { fetchPacks(); }}
        />
      )}
      {editTarget?.pack_type === "VENUE_PACK" && (
        <VenuePackWizard
          key={editTarget.id}
          editPackId={editTarget.id}
          onClose={() => setEditTarget(null)}
          onCreated={() => { fetchPacks(); }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          pack={deleteTarget}
          busy={busyKey === `${deleteTarget.id}:delete`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => handleDelete(deleteTarget)}
        />
      )}

      {flash && (
        <div className={`rounded-md border p-3 text-sm flex items-start gap-2 ${
          flash.ok
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
            : "border-red-500/40 bg-red-500/10 text-red-300"
        }`}>
          {flash.ok && <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />}
          <div className="min-w-0">{flash.msg}</div>
        </div>
      )}

      {/* 2026-07-06 — pack-type filter pills. Client-side filter over
          the already-loaded pack list — pick a bucket to hide the rest.
          "Other" catches TOURNAMENT / SERIES / LEAGUE / TEAMS_ONLY /
          PLAYERS_ONLY and any future type that doesn't have a wizard
          on this page, so the founder can still reach them. */}
      {(() => {
        const OPTIONS = [
          { k: "",                  l: "All" },
          { k: "TOURNAMENT_BUNDLE", l: "Tournament bundles" },
          { k: "SQUAD_PACK",        l: "Squad drafts" },
          { k: "OFFICIALS_PACK",    l: "Officials drafts" },
          { k: "VENUE_PACK",        l: "Venue drafts" },
          { k: "MATCH_BUNDLE",      l: "Match bundles" },
          { k: "__OTHER__",         l: "Other" },
        ];
        const WIZARD_TYPES = new Set([
          "TOURNAMENT_BUNDLE",
          "SQUAD_PACK", "OFFICIALS_PACK", "VENUE_PACK", "MATCH_BUNDLE",
        ]);
        const countFor = (k) => {
          if (!k) return packs.length;
          if (k === "__OTHER__") {
            return packs.filter((p) => !WIZARD_TYPES.has(p.pack_type)).length;
          }
          return packs.filter((p) => p.pack_type === k).length;
        };
        return (
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-text-secondary mr-1">Filter by type:</span>
            {OPTIONS.map((o) => (
              <button
                key={o.k || "all"}
                type="button"
                onClick={() => setPackTypeFilter(o.k)}
                className={`px-3 py-1 rounded-full border transition-colors ${
                  packTypeFilter === o.k
                    ? "bg-button-primary text-white border-button-primary"
                    : "bg-bg-secondary text-text-secondary border-border-default hover:text-text-primary"
                }`}
              >
                {o.l}
                <span className="ml-1.5 font-mono text-[10px] opacity-70">
                  {countFor(o.k)}
                </span>
              </button>
            ))}
          </div>
        );
      })()}

      <div className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-table-header">
            <tr className="text-left text-[11px] uppercase tracking-widest text-text-secondary">
              <th className="px-3 py-2">Pack</th>
              <th className="px-3 py-2">Season</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Players</th>
              <th className="px-3 py-2">Teams</th>
              <th className="px-3 py-2">Officials</th>
              <th className="px-3 py-2">Venues</th>
              <th className="px-3 py-2">Price (ZC)</th>
              <th className="px-3 py-2">Purchases</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && packs.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-8 text-center text-text-secondary">Loading…</td></tr>
            )}
            {!loading && packs.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-8 text-center text-text-secondary">No packs yet.</td></tr>
            )}
            {(() => {
              // 2026-07-06 — client-side pack-type filter empty state.
              const WIZARD_TYPES = new Set([
                "SQUAD_PACK", "OFFICIALS_PACK", "VENUE_PACK", "MATCH_BUNDLE",
              ]);
              const filteredCount = packTypeFilter
                ? (packTypeFilter === "__OTHER__"
                    ? packs.filter((p) => !WIZARD_TYPES.has(p.pack_type)).length
                    : packs.filter((p) => p.pack_type === packTypeFilter).length)
                : packs.length;
              if (!loading && packs.length > 0 && filteredCount === 0) {
                return (
                  <tr>
                    <td colSpan={11} className="px-3 py-8 text-center text-text-secondary">
                      No packs match this type filter. Try “All” to see everything.
                    </td>
                  </tr>
                );
              }
              return null;
            })()}
            {packs
              .filter((p) => {
                // 2026-07-06 — client-side pack-type filter.
                if (!packTypeFilter) return true;
                const WIZARD_TYPES = new Set([
                  "SQUAD_PACK", "OFFICIALS_PACK", "VENUE_PACK", "MATCH_BUNDLE",
                ]);
                if (packTypeFilter === "__OTHER__") return !WIZARD_TYPES.has(p.pack_type);
                return p.pack_type === packTypeFilter;
              })
              .map((p) => (
              <tr key={p.id} className="border-t border-border-default">
                <td className="px-3 py-2 font-medium">
                  {editingNameId === p.id ? (
                    <form
                      onSubmit={(e) => { e.preventDefault(); saveNameEdit(p); }}
                      className="flex items-center gap-1"
                    >
                      <input
                        type="text"
                        maxLength={200}
                        autoFocus
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Escape") cancelNameEdit(); }}
                        disabled={busyKey === `${p.id}:name`}
                        className="w-48 px-1 py-0.5 rounded bg-bg-primary border border-blue-500/50 text-text-primary text-sm font-normal focus:outline-none focus:border-blue-400"
                      />
                      <button
                        type="submit"
                        disabled={busyKey === `${p.id}:name`}
                        className="p-1 rounded text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
                        title="Save (Enter)"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={cancelNameEdit}
                        disabled={busyKey === `${p.id}:name`}
                        className="p-1 rounded text-text-secondary hover:bg-bg-secondary disabled:opacity-50"
                        title="Cancel (Esc)"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startNameEdit(p)}
                      className="group inline-flex items-center gap-1 px-1 py-0.5 rounded hover:bg-bg-secondary text-left"
                      title="Rename this pack (display name only — the slug and any existing links are unchanged)"
                    >
                      <span>{p.name}</span>
                      <Pencil className="w-3 h-3 opacity-40 group-hover:opacity-100 shrink-0" />
                    </button>
                  )}
                </td>
                <td className="px-3 py-2 text-text-secondary">{p.season || "—"}</td>
                <td className="px-3 py-2 text-text-secondary text-xs">{p.pack_type}</td>
                <td className="px-3 py-2 font-mono">{p.player_count}</td>
                <td className="px-3 py-2 font-mono">{p.team_count}</td>
                <td className="px-3 py-2 font-mono">{p.umpire_count ?? 0}</td>
                <td className="px-3 py-2 font-mono">{p.venue_count ?? 0}</td>
                <td className="px-3 py-2 font-mono">
                  {editingPriceId === p.id ? (
                    <form
                      onSubmit={(e) => { e.preventDefault(); savePriceEdit(p); }}
                      className="flex items-center gap-1"
                    >
                      <input
                        type="number"
                        min="0"
                        step="1"
                        autoFocus
                        value={priceDraft}
                        onChange={(e) => setPriceDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Escape") cancelPriceEdit(); }}
                        disabled={busyKey === `${p.id}:price`}
                        className="w-20 px-1 py-0.5 rounded bg-bg-primary border border-blue-500/50 text-text-primary text-sm focus:outline-none focus:border-blue-400"
                      />
                      <button
                        type="submit"
                        disabled={busyKey === `${p.id}:price`}
                        className="p-1 rounded text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
                        title="Save (Enter)"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={cancelPriceEdit}
                        disabled={busyKey === `${p.id}:price`}
                        className="p-1 rounded text-text-secondary hover:bg-bg-secondary disabled:opacity-50"
                        title="Cancel (Esc)"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startPriceEdit(p)}
                      className="group inline-flex items-center gap-1 px-1 py-0.5 rounded hover:bg-bg-secondary"
                      title="Edit price (buyer snapshots are unaffected — PackPurchase records the price they actually paid)"
                    >
                      <span>{p.price_credits}</span>
                      <Pencil className="w-3 h-3 opacity-40 group-hover:opacity-100" />
                    </button>
                  )}
                </td>
                <td className="px-3 py-2 font-mono">{p.purchase_count}</td>
                <td className="px-3 py-2">
                  {p.is_published
                    ? <span className="text-emerald-400 text-xs">● Live</span>
                    : <span className="text-text-secondary text-xs">○ Draft</span>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 justify-end flex-wrap">
                    {EDITABLE_PACK_TYPES.has(p.pack_type) && !p.is_published && (
                      <button
                        onClick={() => setEditTarget(p)}
                        className="flex items-center gap-1 px-2 py-1 rounded border border-border-default hover:bg-bg-secondary text-xs"
                        title="Reopen this draft to change its teams / players / venues before it is bundled and published"
                      >
                        <Pencil className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                    <button
                      disabled={busyKey === `${p.id}:publish`}
                      onClick={() => handlePublishToggle(p)}
                      className={`flex items-center gap-1 px-2 py-1 rounded border text-xs disabled:opacity-50 ${
                        p.is_published
                          ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                          : "border-border-default hover:bg-bg-secondary"
                      }`}
                      title={p.is_published ? "Unpublish (hide from marketplace; existing buyers keep access)" : "Publish (fan out WhatsApp + email + in-app)"}
                    >
                      {p.is_published
                        ? <><EyeOff className="w-3 h-3" /> Unpublish</>
                        : <><Eye className="w-3 h-3" /> Publish</>}
                    </button>
                    <button
                      disabled={busyKey === `${p.id}:delete`}
                      onClick={() => setDeleteTarget(p)}
                      className="flex items-center gap-1 px-2 py-1 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-xs disabled:opacity-50"
                      title="Delete pack — auto-refunds every paid buyer"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function DeleteConfirmModal({ pack, busy, onCancel, onConfirm }) {
  const hasPurchases = (pack.purchase_count || 0) > 0;
  const panelRef = useRef(null);
  const safeCancel = busy ? () => {} : onCancel;
  useDialogDismiss({ open: true, onClose: safeCancel, ref: panelRef });
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Delete pack ${pack.name}`}
        className="bg-bg-primary text-text-primary border border-rose-500/40 rounded-lg w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-border-default">
          <div className="flex items-center gap-2 text-rose-300 font-semibold">
            <AlertTriangle className="w-4 h-4" />
            Delete pack
          </div>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <div>
            You're about to delete <span className="font-semibold">"{pack.name}"</span> ({pack.pack_type}).
          </div>
          <div className="rounded border border-border-default bg-bg-secondary/40 p-3 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-text-secondary">Players</span>
              <span className="font-mono">{pack.player_count || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Teams</span>
              <span className="font-mono">{pack.team_count || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Purchases</span>
              <span className={`font-mono ${hasPurchases ? "text-amber-300" : ""}`}>{pack.purchase_count || 0}</span>
            </div>
          </div>
          {hasPurchases ? (
            <div className="text-xs text-amber-300 leading-relaxed">
              Every paid buyer will be auto-refunded their ZC to their wallet
              via a REVERSAL ledger entry. Free admin-installs are dropped
              without a refund. This is irreversible.
            </div>
          ) : (
            <div className="text-xs text-text-secondary">
              No purchases on this pack. Plain delete — nothing to refund.
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border-default flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 rounded-md border border-border-default hover:bg-bg-secondary text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {busy ? "Deleting…" : hasPurchases ? "Refund + delete" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}
